// @ts-strict-ignore
import {apiClient} from 'common/util/api-client';
import {
  BaseEntityId,
  ProjectShareGrant,
  SharableEntityGrant,
  SharableEntityType,
  TeamShareGrant,
  UserShareGrant,
} from 'common/report/util/saved-entities/types';
import {isTestEnv} from 'common/report/util/env';

import {appApiUrl} from '../utils';
import {deleteFromFetchCache} from '../utils/fetch-cache';

function getSharedEntityEndpointName(sharableEntityType: SharableEntityType): string {
  // Only one that doesn't map to the same name for the endpoint is formulas
  // which are actually modeled as metrics.
  if (sharableEntityType === SharableEntityType.Formula) {
    return SharableEntityType.Metric;
  }
  return sharableEntityType;
}

interface SharableEntityRequest {
  projectId: number;
  entityId: BaseEntityId;
  sharableEntityType: SharableEntityType;
  ignoreCache?: boolean;
}

interface SharableEntityGrantUpdate {
  id: BaseEntityId;
  projectShares?: ProjectShareGrant[];
  userShares?: UserShareGrant[];
  teamShares?: TeamShareGrant[];
}

interface SharableEntityGrantDelete {
  id: BaseEntityId;
  /** Array of project IDs to remove (should only ever be at most 1) */
  projectShares?: number[];
  /** Array of user emails to remove */
  userShares?: string[];
  /** Array of team IDs to remove */
  teamShares?: number[];
}

interface RecentGrants {
  shared: SharableEntityGrant[];
  updated: SharableEntityGrant[];
}

class SharedEntitiesClient {
  async getGrants({
    projectId,
    entityId,
    sharableEntityType,
    ignoreCache = false,
  }: SharableEntityRequest): Promise<SharableEntityGrant> {
    const endpoint = getSharedEntityEndpointName(sharableEntityType);
    const {cache, network} = apiClient.hydratedGet<SharableEntityGrant>(
      appApiUrl(`shared-entities/${endpoint}/${entityId}`, {projectId}),
    );
    const cachedResult = await cache;
    if (cachedResult && !isTestEnv() && !ignoreCache) {
      return cachedResult;
    }
    return network;
  }

  updateGrants(
    {projectId, entityId, sharableEntityType}: SharableEntityRequest,
    {
      projectShares,
      userShares,
      teamShares,
    }: {projectShares?: ProjectShareGrant[]; userShares?: UserShareGrant[]; teamShares?: TeamShareGrant[]},
  ): Promise<SharableEntityGrant> {
    const payload: SharableEntityGrantUpdate = {id: entityId};
    if (projectShares) {
      payload.projectShares = projectShares;
    }
    if (userShares) {
      payload.userShares = userShares;
    }
    if (teamShares) {
      payload.teamShares = teamShares;
    }
    const endpoint = getSharedEntityEndpointName(sharableEntityType);

    // Delete existing value from fetchCache because we are about to modify the object
    deleteFromFetchCache(appApiUrl(`shared-entities/${endpoint}/${entityId}`, {projectId}), {
      method: `GET`,
    });

    return apiClient.post(appApiUrl(`shared-entities/${endpoint}/${entityId}/upsert`, {projectId}), payload);
  }

  removeGrants(
    {projectId, entityId, sharableEntityType}: SharableEntityRequest,
    {projectShares, userShares, teamShares}: {projectShares?: number[]; userShares?: string[]; teamShares?: number[]},
  ): Promise<SharableEntityGrant> {
    const payload: SharableEntityGrantDelete = {id: entityId};
    if (projectShares) {
      payload.projectShares = projectShares;
    }
    if (userShares) {
      payload.userShares = userShares;
    }
    if (teamShares) {
      payload.teamShares = teamShares;
    }
    const endpoint = getSharedEntityEndpointName(sharableEntityType);

    // Delete existing value from fetchCache because we are about to modify the object
    deleteFromFetchCache(appApiUrl(`shared-entities/${endpoint}/${entityId}`, {projectId}), {
      method: `GET`,
    });

    return apiClient.post(appApiUrl(`shared-entities/${endpoint}/${entityId}/delete`, {projectId}), payload);
  }

  async recent({
    projectId,
    sharableEntityType,
  }: {
    projectId: number;
    sharableEntityType: SharableEntityType;
  }): Promise<RecentGrants> {
    const endpoint = getSharedEntityEndpointName(sharableEntityType);
    const {cache, network} = apiClient.hydratedGet<RecentGrants>(
      appApiUrl(`shared-entities/${endpoint}/recent`, {projectId}),
    );
    const cachedResult = await cache;
    if (cachedResult && !isTestEnv()) {
      return cachedResult;
    }
    return network;
  }
}

export const sharedEntitiesClient = new SharedEntitiesClient();
